import { ICellRendererParams } from 'ag-grid-community';
import { Component, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WatchAlert } from '@compumark/order-management-client';

import { AlertApiClientService } from '../../services/alert-api-client.service';
import { toWatchAlertRequestDTO } from '../../services/watch-alert-request-utils';
import { AlertGridOptionsService } from '../alert-column-definition/alert-grid-options.service';

@Component({
  selector: 'app-actions-renderer',
  templateUrl: './actions-renderer.component.html',
  styleUrls: ['./actions-renderer.component.scss'],
  standalone: false,
})
export class ActionsRendererComponent {
  private alertGridOptionsService = inject(AlertGridOptionsService);
  private alertApiClientService = inject(AlertApiClientService);
  private snackBar = inject(MatSnackBar);

  public params!: ICellRendererParams;
  public isSelfRecipient = true;
  public isSystemGenerated = false;
  watchAlert!: WatchAlert;
  private readonly snackBarConfig = {
    duration: 3000,
    panelClass: ['snack-bar'],
  };

  private eventListeners: { type: string; fn: any }[] = [];

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.watchAlert = params.data as WatchAlert;
    this.isSelfRecipient = params.data?.disableSelfReceipt;
    this.isSystemGenerated = params.data?.systemGenerated ?? false;
  }

  get iconData(): { icon: string; toolTip: string } {
    return {
      icon: 'unsubscribe',
      toolTip: 'Re-enable Self-Receipt',
    };
  }
  deleteAlert(): void {
    const alertId = this.params.data?.id;
    const alertTitle = this.params.data?.title;

    if (!alertId) {
      return;
    }
    const api = this.params.api;
    const filteredRowCount = api.getDisplayedRowCount();
    const shouldClearFilters = filteredRowCount === 1;
    const previousAppliedFilters = api.getFilterModel();
    const rowsToRemove = [this.params.data];
    const panelRowNode = this.params.api.getRowNode(
      `${this.params.node.id}__panel`,
    );
    if (panelRowNode) {
      rowsToRemove.push(panelRowNode.data);
    }
    this.params.api.applyTransaction({ remove: rowsToRemove });
    if (shouldClearFilters) {
      this.alertGridOptionsService.clearAlertFilters();
    }
    const snackBarRef = this.snackBar.open(
      `Alert "${alertTitle}" has been deleted`,
      'Undo',
      this.snackBarConfig,
    );
    let undoClicked = false;

    snackBarRef.onAction().subscribe(() => {
      undoClicked = true;
      this.params.api.applyTransaction({ add: rowsToRemove });
      if (undoClicked) {
        api.setFilterModel(previousAppliedFilters);
      }
    });

    snackBarRef.afterDismissed().subscribe(() => {
      if (!undoClicked) {
        this.alertApiClientService.deleteAlert(alertId).subscribe(() => {
          this.alertGridOptionsService.refreshGrid();
        });
      }
    });
  }

  disableSelfRecipient(): void {
    this.isSelfRecipient = !this.isSelfRecipient;
    this.watchAlert.disableSelfReceipt = this.isSelfRecipient;
    this.alertApiClientService
      .updateAlert(this.watchAlert)
      .subscribe((updated) => {
        this.params.data.disableSelfReceipt = updated.disableSelfReceipt;
      });
  }

  private closeAllOpenPanelsforSysytemAlerts(): void {
    const api = this.params.api;
    const panelRowsToRemove: any[] = [];

    api.forEachNode((node: any) => {
      if (node.data?._panelOpen && node.data?.systemGenerated === true) {
        node.data._panelOpen = false;

        const panelId = `${node.data.id}__panel`;
        const panelNode = api.getRowNode(panelId);
        if (panelNode) {
          panelRowsToRemove.push(panelNode.data);
        }
      }
    });
    if (panelRowsToRemove.length) {
      api.applyTransaction({ remove: panelRowsToRemove });
    }
    api.refreshCells({ force: true });
  }

  duplicateAlert(): void {
    const wa = this.watchAlert;
    const requestDTO = toWatchAlertRequestDTO(wa);

    this.alertApiClientService.createAlert(requestDTO as any).subscribe({
      next: (duplicatedAlertDTO: any) => {
        this.alertGridOptionsService.clearAlertFilters();
        this.closeAllOpenPanelsforSysytemAlerts();
        if (wa.systemGenerated) {
          this.params.api.applyTransaction({
            remove: [this.params.api.getRowNode(wa.id!)?.data],
          });
        }
        duplicatedAlertDTO._highlighted = true;
        duplicatedAlertDTO._focusOnDuplicate = true;

        const result = this.params.api.applyTransaction({
          add: [duplicatedAlertDTO],
        });

        if (result?.add?.length) {
          const node = result.add[0];
          if (node.rowIndex !== null) {
            this.params.api.ensureIndexVisible(node.rowIndex, 'middle');
          }
        }
        this.scrollToDuplicatedAlert(duplicatedAlertDTO.id);
        const removeHighlight = () => {
          this.params.api.forEachNode((rowNode) => {
            if (rowNode.data?._highlighted) {
              rowNode.data._highlighted = false;
            }
          });
          setTimeout(() => {
            this.params.api.redrawRows();
          }, 400);
          this.removeEventListeners();
        };

        this.removeEventListeners();

        this.eventListeners = [{ type: 'sortChanged', fn: removeHighlight }];

        this.eventListeners.forEach((l) =>
          this.params.api.addEventListener(l.type as any, l.fn),
        );

        this.snackBar.open(
          `"${duplicatedAlertDTO.title}" created successfully.`,
          'Close',
          this.snackBarConfig,
        );
      },
    });
  }
  /** remove old listeners to prevent leaks */
  private removeEventListeners(): void {
    if (this.eventListeners?.length) {
      this.eventListeners.forEach((l) =>
        this.params.api.removeEventListener(l.type as any, l.fn),
      );
      this.eventListeners = [];
    }
  }
  private scrollToDuplicatedAlert(alertId: string): void {
    setTimeout(() => {
      const rowNode = this.params.api.getRowNode(alertId);
      if (rowNode) {
        this.params.api.ensureNodeVisible(rowNode, 'middle');
      }
    }, 100);
  }
}
